import type {PayerDto} from '@/ts/types/dto/insurance-dto';

// The payer directory is a large (~2.7MB) list that rarely changes — the backend serves the full
// directory and caches it for 24h. The verification dialog is v-if-remounted on every open, so
// without this it re-downloads and re-parses the whole list each time. Cache it at module scope
// (i.e. for the current page visit) so the first open fetches it and later opens reuse it. A
// failed fetch is not cached, so it retries on the next open.
let cached: PayerDto[] | null = null;
let inFlight: Promise<PayerDto[]> | null = null;

/**
 * Returns the payer directory, fetching it via `fetchDirectory` only on the first call (or after a
 * failure / a `clearCachedPayerDirectory`); later calls resolve from the module cache. Concurrent
 * calls share a single in-flight request.
 */
export async function loadCachedPayerDirectory(fetchDirectory: () => Promise<PayerDto[]>): Promise<PayerDto[]> {
  if (cached) {
    return cached;
  }
  if (!inFlight) {
    inFlight = fetchDirectory()
      .then((payers) => {
        cached = payers;
        return payers;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** Clears the cached directory. Used by tests; also a hook if a forced refresh is ever needed. */
export function clearCachedPayerDirectory(): void {
  cached = null;
  inFlight = null;
}
